'use client'

import { Input } from '@/components/ui/input'
import type { ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'
import { watchClasses } from '../ProcessedEntry/DomainEntry/processedTypes'
import { SearchableSelect } from './SearchableSelect'
import type { Catalog } from './entryTypes'

// Same platform/message-type assignment CasosView's "Feed" collapsible
// offers (handleEditRawField), pulled out into a standalone widget so any
// entry-detail page can drop it in rather than re-implementing the two
// <select>s inline. Purely controlled: the caller owns platformId/
// messageTypeId and decides what onAssign does with a change (stage it
// locally, sync immediately if the entry is already posted, etc).
export function AssignFieldsWidget({
  theme,
  catalog,
  postType,
  messageTypeId,
  platformId,
  platformText,
  onAssign,
  onPlatformTextChange,
}: {
  theme: ProcessedTheme
  catalog: Catalog
  /** 'call' selects catalog.callTypes for the message-type dropdown, everything else uses catalog.messageTypes. */
  postType?: string | null
  messageTypeId?: number | string | null
  platformId?: number | string | null
  /** Free-text fallback for when no catalog.platforms entry fits. */
  platformText?: string | null
  onAssign: (field: 'platform' | 'messageType', id?: string, text?: string) => void
  /** Kept separate from onAssign: a manual edit is staged locally like EditInputWidget's fields rather than synced on every keystroke — see the hint text next to the input. */
  onPlatformTextChange: (value: string) => void
}) {
  const c = watchClasses(theme)
  const isCall = postType === 'call'
  const typeList = isCall ? catalog.callTypes : catalog.messageTypes

  return (
    <div className="space-y-3">
      <div>
        <div className={`mb-1 text-[11px] font-semibold ${c.label}`}>{isCall ? 'Call type' : 'Message type'}</div>
        <SearchableSelect
          theme={theme}
          value={messageTypeId}
          options={typeList}
          placeholder="Select type…"
          searchPlaceholder="Search types…"
          onChange={(id, text) => onAssign('messageType', id, text)}
        />
      </div>
      <div>
        <div className={`mb-1 text-[11px] font-semibold ${c.label}`}>Platform</div>
        <SearchableSelect
          theme={theme}
          value={platformId}
          options={catalog.platforms}
          placeholder="Select platform…"
          searchPlaceholder="Search platforms…"
          noneLabel="No platform"
          onChange={(id, text) => onAssign('platform', id, text)}
        />
        <Input
          className={`mt-1.5 h-7 text-[11px] ${c.input}`}
          value={platformText ?? ''}
          onChange={(e) => onPlatformTextChange(e.target.value)}
          placeholder="Or type manually if none fit…"
        />
        <p className={`mt-1 text-[10px] ${c.faint}`}>Changes apply when you click Accept.</p>
      </div>
    </div>
  )
}
